import { 
  Component,
  EventEmitter,
  OnInit,
  ViewEncapsulation,
  ViewChild,
  ChangeDetectionStrategy,
  AfterViewInit,
  Output,
  Renderer2,
  ChangeDetectorRef,
  OnDestroy, 
  Input,
  ElementRef} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Subject, Observable, fromEvent, concat, combineLatest } from 'rxjs';
import { debounceTime, delay, filter, map, takeUntil } from 'rxjs/operators';
import { ViewState } from '../../state/view.state';
import { ViewMode } from '../../models/view-mode.enum';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ViewService } from '../../view.service';
import { ViewToolsMode } from '../../models/view-tools-mode.enum';
import { ViewUIModel } from '../../models/view-ui.model';
import { ViewUIService } from '../../view-ui.service';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { environment } from 'src/environments/environment';
import { LayoutState } from 'src/app/layout/state/layout.state';
import { SiteHeaderType } from 'src/app/layout/models/site-header-type.enum';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { QuizState } from 'src/app/quiz/state/quiz.state';
import { QuizSettingsModel } from 'src/app/quiz/models/quiz-settings.model';
import { QuizService } from '../view-quiz/view-quiz.service';
import { ChapterReadyStateModel } from 'src/app/story/models/chapter-ready-state.model';

const drawerHeight = 64;
const halfwayPoint = 0.5;

interface Breakpoint {
  velocity: number;
  point: number;
}

@Component({
  selector: 'app-view-drawer',
  templateUrl: './view-drawer.component.html',
  styleUrls: ['./view-drawer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewDrawerComponent implements OnInit, AfterViewInit, OnDestroy {
  // ---------- Child Elements ----------
  @ViewChild('viewDrawer', {static: false }) viewDrawer: ElementRef<HTMLDivElement>;
  @ViewChild('viewDrawerOuter', {static: false }) viewDrawerOuter: any;



  // ---------- Observables ----------
  @Select(ViewState.getUI)
  layout$: Observable<ViewUIModel>;

  @Select(StoryChaptersState.getSelectedChapterReadyState)
  selectedChapterReady$: Observable<ChapterReadyStateModel>;

  @Select(StoryChaptersState.getSelectedChapterIndex)
  chapterIndex$: Observable<number>;

  @Select(StoryChaptersState.getChapterCount)
  chapterCount$: Observable<number>;

  @Select(StoryLabelsState.getSelectedLabel)
  selectedLabel$: Observable<LabelModel>;

  @Select(QuizState.getQuizModeActive)
  quizModeActive$: Observable<boolean>;

  @Select(QuizState.getActiveQuestionIndex)
  activeQuestionIndex$: Observable<number>;

  @Select(QuizState.getQuestionTotal)
  questionTotal$: Observable<number>;

  @Select(QuizState.getQuestionsRemaining)
  questionRemaining$: Observable<number>;
  
  @Select(QuizState.getQuizSettings)
  quizSettings$: Observable<QuizSettingsModel>;

  @Select(QuizState.getQuizModeLocked)
  quizLocked$: Observable<boolean>;

  // ---------- Input / Output ----------
  @Input()
  public get expandable() {
    return this.layoutService.getVisibleContent() === 'full' 
      || this.mode === ViewMode.LabelInfo 
      || this.mode === ViewMode.NodeInfo 
      || this.open;
  }

  @Output() 
  offsetYChange = new EventEmitter<number>();


  // ---------- Class Variables ----------
  drawerY: number;

  breakpoints: { [key: string]: Breakpoint } = {
    minimumDrawer: {
      velocity: Infinity,
      point: 0
    },
    halfDrawer: {
      velocity: 1,
      point: 0
    }
  };

  ngUnsubscribe = new Subject();
  viewMode = ViewMode;
  viewToolsMode = ViewToolsMode;
  mode: ViewMode = ViewMode.Content;
  open = false;

  
  // ---------- Lifecycle ----------
  constructor(
    private renderer: Renderer2,
    private changeDetectorRef: ChangeDetectorRef,
    private viewService: ViewService,
    private quizService: QuizService,
    private store: Store,
    private layoutService: ViewUIService
  ) { }

  ngOnInit() {
    this.initWindowSizeObserver();
    this.initLayoutObserver();
    this.initQuizObserver();
  }
  
  ngAfterViewInit() {
    this.refresh(this.store.selectSnapshot(ViewState.getUI).drawer.isExpandedInitially);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // ---------- Layout ----------
  initWindowSizeObserver() {
    // reset layout on sizing changes
    concat(
      fromEvent(window, 'resize'),
      fromEvent(window, 'orientationchange')
    )
      .pipe(
        delay(0),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.refresh(false);
      });
  }

  initLayoutObserver() {
    this.layout$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(layout => {
      this.updateMode(layout);
    });
  }

  initQuizObserver() {
    // detect chapter ready, wait for model to render
    const chapterReady$ = this.selectedChapterReady$
      .pipe(
        map((readyState) => readyState && readyState.model),
        filter((ready) => ready),
        debounceTime(300)
      );

    // open drawer when quiz lanched 
    combineLatest([
      // chapter ready
      chapterReady$,
      // quiz active
      this.quizModeActive$
    ])
      .pipe(
        filter(([ready, quizActive]) => ready && quizActive),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        setTimeout(() => {
          this.toggleDrawer(true);
          this.changeDetectorRef.markForCheck();
        });
      });
  }

  /**
   * Mobile devices have variable viewheight because the browser bar can scroll in and out of view
   * This workaround determines the available window space and uses that as a viewheight unit
   */
  updateViewHeight() {
    const includeOffset = environment.application !== EnvironmentApplication.Viewer;
    const vh = this.viewService.getVH(includeOffset);
    this.viewDrawer.nativeElement.style.setProperty('--vh', `${vh}px`);
  }

  updateStops() {
    const windowHeight = window.innerHeight;

    const siteHeader = this.store.selectSnapshot(LayoutState.getSiteHeader);
    const topOffset = siteHeader === SiteHeaderType.Hide ? 0 : 56;

    this.breakpoints.minimumDrawer.point = -drawerHeight;
    this.breakpoints.halfDrawer.point = Math.floor(-((windowHeight - topOffset) * halfwayPoint));
  }

  updateMode(layout: ViewUIModel) {
    this.mode = this.viewService.inferDrawerMode(layout);
    this.changeDetectorRef.markForCheck();
  }

  resetDrawerPosition(expanded: boolean) {
    setTimeout(() => {
      const point = expanded 
        ? this.breakpoints.halfDrawer.point 
        : this.breakpoints.minimumDrawer.point;

      this.slideDrawerToPoint(point);

      // set open state
      this.open = expanded;
      this.changeDetectorRef.markForCheck();
    });
  }

  refresh(expanded: boolean) {
    setTimeout(() => {
      this.updateViewHeight();
      this.updateStops();
      this.resetDrawerPosition(expanded);
    });
  }

  

  // ---------- Interaction ----------
  handleToggleClick() {
    if (this.expandable) {
      this.toggleDrawer();
    }
  }

  handleDrawerKeydown(event: KeyboardEvent) {
    // Prevent keyboard scrolling on content from trigger camera movement
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.stopPropagation();
    }
  }

  handleLabelBack() {
    this.viewService.clearSelectedLabel();
  }

  // ---------- Utility ----------
  public toggleDrawer(open?: boolean) {
    if (open == null) {
      open = !this.open;
    }

    const breakpoint = open ? this.breakpoints.halfDrawer : this.breakpoints.minimumDrawer;
    this.slideDrawerToPoint(breakpoint.point);

    this.open = open;

    this.open ? 
      this.viewDrawerOuter.nativeElement.focus() :
      this.viewDrawerOuter.nativeElement.blur();
  }

  handleExitQuiz() {
    this.quizService.exitQuiz();
  }

  slideDrawerToPoint(y: number) {
    this.renderer.setStyle(this.viewDrawer.nativeElement, 'transform', `translate3d(0, ${y}px, 0)`);
    this.offsetYChange.emit(y);
  }

  getDrawerY() {
    if (this.viewDrawer) {
      const transform = this.viewService.getComputedTransform(this.viewDrawer.nativeElement);
      return transform ? transform.y : null;
    } else {
      return null;
    }
  }
}
